import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckSquare, 
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
  Image,
  Target,
  Globe,
  Filter,
  Search,
  Loader2,
  Sparkles,
  Eye,
  ExternalLink,
  MessageSquare,
  Send,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { api, useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

/* Map an approval's status to the list tab that contains it, so a deep-linked
   item opens on a tab where it's actually visible. A client never sees their
   own client_approved items in a status tab, so fall back to "all" there. */
function statusToTab(status, isClient) {
  if (status === 'approved') return 'approved';
  if (status === 'rejected' || status === 'client_rejected') return 'rejected';
  if (isClient && status === 'client_approved') return 'all';
  return 'pending';
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return 'Recently';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function ApprovalsPage() {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  /* Stage-2 final-approval eligibility by item type: PM/Admin always, plus the
     domain owner (content/creative accounts for editorial items, execution team
     for technical competitor-replication changes). Mirrors the backend rule. */
  const canApproveType = (type) => {
    const role = user?.role;
    if (type === 'competitor_replicate') {
      return ['admin', 'project_manager', 'execution_team'].includes(role);
    }
    return ['admin', 'project_manager', 'content_account', 'creative_account'].includes(role);
  };
  const [approvals, setApprovals] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [selectedTab, setSelectedTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewDetail, setViewDetail] = useState(null);
  const [suggestionText, setSuggestionText] = useState('');
  const [sendingSuggestion, setSendingSuggestion] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const handledDeepLink = useRef(false);

  // Deep link from a notification (?approval_id=…): open that item's dialog and
  // switch to the tab where it lives, instead of dumping the user on Pending.
  useEffect(() => {
    const deepLinkId = searchParams.get('approval_id');
    if (!deepLinkId || handledDeepLink.current) return;
    handledDeepLink.current = true;
    (async () => {
      setViewDialogOpen(true);
      setViewDetail(null);
      setViewLoading(true);
      try {
        const res = await api.get(`/approvals/${deepLinkId}/item-detail`);
        setViewDetail(res.data);
        setSelectedTab(statusToTab(res.data?.approval?.status, isClient));
      } catch {
        toast.error('Could not open the linked approval');
        setViewDialogOpen(false);
      } finally {
        setViewLoading(false);
      }
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('approval_id');
        return next;
      }, { replace: true });
    })();
  }, [searchParams, isClient, setSearchParams]);

  useEffect(() => {
    const fetchApprovals = async () => {
      try {
        const response = await api.get(`/approvals?page=${page}&page_size=${pageSize}`);
        setApprovals(response.data.items || []);
        setTotal(response.data.total || 0);
        setHasMore(response.data.has_more || false);
      } catch (error) {
        console.error('Error fetching approvals:', error);
        toast.error('Failed to load approvals');
      } finally {
        setFetching(false);
      }
    };
    fetchApprovals();
  }, [page]);

  const getTypeIcon = (type) => {
    const icons = {
      content: FileText,
      creative: Image,
      keyword: Target,
      gbp_post: Globe,
      onpage: CheckSquare,
      competitor: Target,
      competitor_replicate: Sparkles,
    };
    return icons[type] || FileText;
  };

  const getTypeBadge = (type) => {
    const types = {
      content: { label: 'Content', color: 'bg-indigo-100 text-indigo-700' },
      creative: { label: 'Creative', color: 'bg-pink-100 text-pink-700' },
      keyword: { label: 'Keyword', color: 'bg-cyan-100 text-cyan-700' },
      gbp_post: { label: 'GBP Post', color: 'bg-emerald-100 text-emerald-700' },
      onpage: { label: 'On-Page', color: 'bg-violet-100 text-violet-700' },
      competitor: { label: 'Competitor', color: 'bg-amber-100 text-amber-700' },
      competitor_replicate: { label: 'Replicate', color: 'bg-amber-100 text-amber-700' },
    };
    return types[type] || types.content;
  };

  const filteredApprovals = approvals.filter(a => {
    const matchesSearch = a.title.toLowerCase().includes(searchTerm.toLowerCase());
    if (selectedTab === 'all') return matchesSearch;
    if (selectedTab === 'pending') {
      if (isClient) {
        return matchesSearch && a.status === 'pending';
      } else {
        return matchesSearch && (a.status === 'pending' || a.status === 'client_approved');
      }
    }
    if (selectedTab === 'approved') return matchesSearch && a.status === 'approved';
    if (selectedTab === 'rejected') return matchesSearch && (a.status === 'rejected' || a.status === 'client_rejected');
    return matchesSearch && a.status === selectedTab;
  });

  const handleApprove = async (approval) => {
    const approvalKey = approval.approval_id;
    setLoadingId(approvalKey);
    try {
      const res = await api.post(`/approvals/${approvalKey}/approve`);
      const newStatus = isClient ? 'client_approved' : 'approved';
      setApprovals(prev => prev.map(a =>
        a.approval_id === approvalKey
          ? { ...a, status: newStatus }
          : a
      ));
      toast.success(res.data?.message || 'Approved successfully!');
      setSelectedApproval(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Failed to approve';
      toast.error(msg);
    } finally {
      setLoadingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedApproval) return;
    const approvalKey = selectedApproval.approval_id;
    setLoadingId(approvalKey);
    try {
      await api.post(`/approvals/${approvalKey}/reject`, null, {
        params: { reason: rejectReason }
      });
      const newStatus = isClient ? 'client_rejected' : 'rejected';
      setApprovals(prev => prev.map(a =>
        a.approval_id === approvalKey
          ? { ...a, status: newStatus }
          : a
      ));
      toast.success('Rejected');
      setRejectDialogOpen(false);
      setSelectedApproval(null);
      setRejectReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setLoadingId(null);
    }
  };

  const pendingCount = approvals.filter(a => {
    if (isClient) {
      return a.status === 'pending';
    }
    return a.status === 'pending' || a.status === 'client_approved';
  }).length;

  const handleSendSuggestion = async (approvalId) => {
    if (!suggestionText.trim()) return;
    setSendingSuggestion(true);
    try {
      const res = await api.post(`/approvals/${approvalId}/suggestions`, { suggestion: suggestionText });
      if (viewDetail) {
        const existing = viewDetail.approval?.suggestions || [];
        setViewDetail(prev => ({
          ...prev,
          approval: { ...prev.approval, suggestions: [...existing, res.data.suggestion] },
        }));
      }
      setSuggestionText('');
      toast.success('Suggestion added');
    } catch {
      toast.error('Failed to add suggestion');
    } finally {
      setSendingSuggestion(false);
    }
  };

  const handleViewItem = async (approval) => {
    setViewDialogOpen(true);
    setViewDetail(null);
    setViewLoading(true);
    try {
      const res = await api.get(`/approvals/${approval.approval_id || approval.id}/item-detail`);
      setViewDetail(res.data);
    } catch {
      toast.error('Failed to load content details');
      setViewDialogOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statuses = {
      draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700' },
      ai_generated: { label: 'AI Generated', color: 'bg-violet-100 text-violet-700' },
      humanized: { label: 'Humanized', color: 'bg-purple-100 text-purple-700' },
      ready_for_approval: { label: 'In Review', color: 'bg-amber-100 text-amber-700' },
      pending: { label: 'Pending Client Review', color: 'bg-amber-100 text-amber-700' },
      client_approved: { label: 'Client Approved', color: 'bg-blue-100 text-blue-700' },
      client_rejected: { label: 'Client Rejected', color: 'bg-red-100 text-red-700' },
      approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
      published: { label: 'Published', color: 'bg-emerald-100 text-emerald-700' },
      rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
      pending_approval: { label: 'In Review', color: 'bg-amber-100 text-amber-700' },
    };
    return statuses[status] || statuses.draft;
  };

  const getApprovalStatusBadge = (status) => {
    const map = {
      pending: { label: 'Awaiting Client', color: 'bg-amber-100 text-amber-700' },
      client_approved: { label: 'Client Approved – Awaiting PM', color: 'bg-blue-100 text-blue-700' },
      client_rejected: { label: 'Client Rejected', color: 'bg-red-100 text-red-700' },
      approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
      rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
    };
    return map[status] || map.pending;
  };

  if (fetching) {
    return (
      <div className="space-y-8" data-testid="approvals-page-loading">
        <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        <div className="grid grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="approvals-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Approvals</h1>
          <p className="text-slate-600 dark:text-slate-400">
            {pendingCount} items pending your review
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search approvals..."
              className="pl-9 w-64"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              data-testid="approval-search-input"
            />
          </div>
          <Button variant="outline" size="icon">
            <Filter className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-6">
        {[
          { label: 'Pending', count: pendingCount, icon: Clock, color: 'amber' },
          { label: 'Approved', count: approvals.filter(a => a.status === 'approved').length, icon: CheckCircle2, color: 'emerald' },
          { label: 'Rejected', count: approvals.filter(a => a.status === 'rejected' || a.status === 'client_rejected').length, icon: XCircle, color: 'red' },
        ].map((stat, i) => (
          <Card key={stat.label} className="gds-card" data-testid={`approval-stat-${i}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{stat.label}</p>
                  <p className="text-3xl font-bold font-heading mt-1">{stat.count}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Approvals List */}
      <Card className="gds-card">
        <CardHeader>
          <Tabs value={selectedTab} onValueChange={(v) => { setSelectedTab(v); setPage(1); }}>
            <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
              <TabsTrigger value="pending" className="rounded-lg">
                Pending
                {pendingCount > 0 && (
                  <Badge variant="destructive" className="ml-2">{pendingCount}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="approved" className="rounded-lg">Approved</TabsTrigger>
              <TabsTrigger value="rejected" className="rounded-lg">Rejected</TabsTrigger>
              <TabsTrigger value="all" className="rounded-lg">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredApprovals.map((approval, i) => {
              const itemType = approval.type || approval.item_type;
              const TypeIcon = getTypeIcon(itemType);
              const isReplicateItem = itemType === 'competitor_replicate';
              const canInternalApprove =
                approval.status === 'client_approved'
                || (!isReplicateItem && approval.status === 'pending');
              const iCanApprove = !isClient && canInternalApprove && canApproveType(itemType);
              const clientCanAct = isClient && approval.status === 'pending';
              return (
                <motion.div
                  key={approval.id || approval.approval_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: i * 0.05 }}
                  className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer group"
                  onClick={() => handleViewItem(approval)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      approval.type === 'content' || approval.item_type === 'content' ? 'bg-indigo-100 text-indigo-600' :
                      approval.type === 'gbp_post' || approval.item_type === 'gbp_post' ? 'bg-emerald-100 text-emerald-600' :
                      approval.type === 'competitor_replicate' || approval.item_type === 'competitor_replicate' ? 'bg-amber-100 text-amber-600' :
                      approval.type === 'keyword' || approval.item_type === 'keyword' ? 'bg-cyan-100 text-cyan-600' :
                      'bg-violet-100 text-violet-600'
                    }`}>
                      <TypeIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium flex items-center gap-1.5">
                        {approval.title}
                        <Eye className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={getTypeBadge(approval.type || approval.item_type).color}>
                          {getTypeBadge(approval.type || approval.item_type).label}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {approval.requested_by_name || approval.requestedBy || approval.requested_by || 'Team'} • {formatTimeAgo(approval.requestedAt || approval.requested_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                    {/* Client sees buttons on 'pending' items; Internal sees on 'pending' and 'client_approved' */}
                    {isClient && approval.status === 'pending' && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedApproval(approval);
                            setRejectDialogOpen(true);
                          }}
                          data-testid={`reject-btn-${i}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                        <Button 
                          size="sm" 
                          className="gds-btn-primary"
                          onClick={() => handleApprove(approval)}
                          disabled={loadingId === approval.approval_id}
                          data-testid={`approve-btn-${i}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      </>
                    )}
                    {iCanApprove && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedApproval(approval);
                            setRejectDialogOpen(true);
                          }}
                          data-testid={`reject-btn-${i}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                        <Button 
                          size="sm" 
                          className="gds-btn-primary"
                          onClick={() => handleApprove(approval)}
                          disabled={loadingId === approval.approval_id}
                          data-testid={`approve-btn-${i}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          {approval.status === 'client_approved' ? 'Final Approve' : 'Approve'}
                        </Button>
                      </>
                    )}
                    {/* Status badge whenever the current user has no action to take */}
                    {!clientCanAct && !iCanApprove && (
                      <Badge className={getApprovalStatusBadge(approval.status).color}>
                        {getApprovalStatusBadge(approval.status).label}
                      </Badge>
                    )}
                  </div>
                </motion.div>
              );
            })}
            
            {filteredApprovals.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <CheckSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No approvals found</p>
              </div>
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Page {page} of {Math.ceil(total / pageSize)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={!hasMore}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Approval</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting "{selectedApproval?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              data-testid="reject-reason-input"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={!!loadingId}
              data-testid="confirm-reject-btn"
            >
              Reject
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Content Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          {viewLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mb-3" />
              <p className="text-sm text-slate-500">Loading content...</p>
            </div>
          ) : viewDetail ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-slate-900 dark:text-white">
                  {viewDetail.approval?.title || viewDetail.item_detail?.title || 'Content Details'}
                </DialogTitle>
                <DialogDescription>
                  {viewDetail.approval?.item_type === 'gbp_post'
                    ? 'GBP Post submitted for approval'
                    : viewDetail.approval?.item_type === 'competitor_replicate'
                      ? 'Replicate request submitted for approval'
                      : 'Content submitted for approval'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={getTypeBadge(viewDetail.approval?.item_type || 'content').color}>
                    {getTypeBadge(viewDetail.approval?.item_type || 'content').label}
                  </Badge>
                  {viewDetail.item_detail?.status && (
                    <Badge className={getStatusBadge(viewDetail.item_detail.status).color}>
                      {getStatusBadge(viewDetail.item_detail.status).label}
                    </Badge>
                  )}
                  {viewDetail.item_detail?.seo_score && (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      SEO: {viewDetail.item_detail.seo_score}/100
                    </Badge>
                  )}
                  {viewDetail.approval?.requested_by_name && (
                    <span className="text-xs text-slate-500">
                      Submitted by {viewDetail.approval.requested_by_name} • {formatTimeAgo(viewDetail.approval.requested_at)}
                    </span>
                  )}
                </div>

                {/* Keywords for content */}
                {viewDetail.item_detail?.target_keywords?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Target Keywords</p>
                    <div className="flex flex-wrap gap-2">
                      {viewDetail.item_detail.target_keywords.map((kw, i) => (
                        <Badge key={i} variant="outline">{kw}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Replicate preview */}
                {viewDetail.approval?.item_type === 'competitor_replicate' && viewDetail.item_detail && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                        <p className="text-xs font-medium text-red-600 mb-1">Competitor Has</p>
                        <p className="text-sm text-red-700 dark:text-red-300">
                          {viewDetail.item_detail?.win_point?.competitor_has || 'Not provided'}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                        <p className="text-xs font-medium text-amber-600 mb-1">You Lack</p>
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                          {viewDetail.item_detail?.win_point?.you_lack || 'Not provided'}
                        </p>
                      </div>
                    </div>
                    <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
                      <p className="text-xs font-medium text-indigo-600 mb-1">Action</p>
                      <p className="text-sm text-indigo-700 dark:text-indigo-300">
                        {viewDetail.item_detail?.win_point?.action || viewDetail.approval?.description || 'Not provided'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {viewDetail.item_detail?.risk_level && (
                        <Badge className={
                          viewDetail.item_detail.risk_level === 'low'
                            ? 'bg-emerald-100 text-emerald-700'
                            : viewDetail.item_detail.risk_level === 'high'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700'
                        }>
                          Risk: {viewDetail.item_detail.risk_level}
                        </Badge>
                      )}
                      {viewDetail.item_detail?.change_type && (
                        <Badge variant="outline">Change: {viewDetail.item_detail.change_type}</Badge>
                      )}
                      <Badge variant="outline">
                        {viewDetail.item_detail?.auto_applicable
                          ? 'Will auto-apply after final approval'
                          : 'Will create manual task after approval'}
                      </Badge>
                    </div>
                    {(viewDetail.item_detail?.my_url || viewDetail.item_detail?.target_url) && (
                      <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                        <p className="text-xs font-medium text-slate-600 mb-1">Target URL</p>
                        <p className="text-sm text-slate-700 dark:text-slate-300">
                          {viewDetail.item_detail?.target_url || viewDetail.item_detail?.my_url}
                        </p>
                      </div>
                    )}
                    {(viewDetail.item_detail?.win_point?.current_value || viewDetail.item_detail?.win_point?.code_snippet) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                          <p className="text-xs font-medium text-slate-600 mb-1">Current Value</p>
                          <pre className="text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-all max-h-48 overflow-auto">
                            {viewDetail.item_detail?.win_point?.current_value || 'Not provided'}
                          </pre>
                        </div>
                        <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                          <p className="text-xs font-medium text-emerald-700 mb-1">Proposed Change</p>
                          <pre className="text-xs text-emerald-800 dark:text-emerald-300 whitespace-pre-wrap break-all max-h-48 overflow-auto">
                            {viewDetail.item_detail?.win_point?.code_snippet || 'Not provided'}
                          </pre>
                        </div>
                      </div>
                    )}
                    {viewDetail.item_detail?.execution_mode && (
                      <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                        <p className="text-xs font-medium text-slate-600 mb-1">Execution Result</p>
                        <p className="text-sm text-slate-700 dark:text-slate-300">
                          {viewDetail.item_detail.execution_mode === 'auto_applied'
                            ? 'Applied automatically via CMS integration'
                            : 'Routed to manual implementation task'}
                        </p>
                        {viewDetail.item_detail.task_id && (
                          <p className="text-xs text-slate-500 mt-1">Task ID: {viewDetail.item_detail.task_id}</p>
                        )}
                        {viewDetail.item_detail.auto_skip_reason && (
                          <p className="text-xs text-slate-500 mt-1">
                            Reason: {viewDetail.item_detail.auto_skip_reason}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Content body - content type */}
                {viewDetail.approval?.item_type === 'content' && viewDetail.item_detail && (
                  (viewDetail.item_detail.draft || viewDetail.item_detail.brief) ? (
                    <div className="space-y-4">
                      {viewDetail.item_detail.draft && (
                        <div>
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-indigo-500" />
                            {viewDetail.item_detail.brief ? 'Final Draft' : 'Content'}
                          </p>
                          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 prose prose-base max-w-none dark:prose-invert max-h-[50vh] overflow-y-auto prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-li:my-0.5">
                            <ReactMarkdown>{viewDetail.item_detail.draft}</ReactMarkdown>
                          </div>
                        </div>
                      )}
                      {viewDetail.item_detail.brief && (
                        <div>
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-violet-500" />
                            {viewDetail.item_detail.draft ? 'Original / AI Generated' : 'Content'}
                          </p>
                          <div className={`p-4 rounded-xl border prose prose-base max-w-none dark:prose-invert max-h-[50vh] overflow-y-auto prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-li:my-0.5 ${
                            viewDetail.item_detail.draft
                              ? 'bg-slate-50/50 dark:bg-slate-900/30 border-slate-100 dark:border-slate-800'
                              : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
                          }`}>
                            <ReactMarkdown>{viewDetail.item_detail.brief}</ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No content body available.</p>
                    </div>
                  )
                )}

                {/* Content body - GBP post type */}
                {viewDetail.approval?.item_type === 'gbp_post' && viewDetail.item_detail && (
                  (viewDetail.item_detail.summary || viewDetail.item_detail.content) ? (
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                        <Globe className="w-4 h-4 text-emerald-500" />
                        Post Content
                      </p>
                      <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 prose prose-base max-w-none dark:prose-invert max-h-[50vh] overflow-y-auto prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-li:my-0.5">
                        <ReactMarkdown>{viewDetail.item_detail.summary || viewDetail.item_detail.content}</ReactMarkdown>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Globe className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No post content available.</p>
                    </div>
                  )
                )}

                {/* No item found fallback */}
                {!viewDetail.item_detail && (
                  <div className="text-center py-8 text-slate-400">
                    <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Content details could not be loaded.</p>
                  </div>
                )}

                {/* Published URL */}
                {viewDetail.item_detail?.published_url && (
                  <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <p className="text-xs font-medium text-emerald-600 mb-1">Published URL</p>
                    <a href={viewDetail.item_detail.published_url} target="_blank" rel="noopener noreferrer"
                      className="text-sm text-emerald-700 hover:underline flex items-center gap-1">
                      {viewDetail.item_detail.published_url}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                )}

                {/* Rejection reason if rejected */}
                {viewDetail.approval?.rejection_reason && (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <p className="text-xs font-medium text-red-600 mb-1">Rejection Reason</p>
                    <p className="text-sm text-red-700 dark:text-red-300">{viewDetail.approval.rejection_reason}</p>
                  </div>
                )}

                {/* Two-stage approval status */}
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                  <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Approval Pipeline</p>
                  <div className="flex items-center gap-2">
                    <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      viewDetail.approval?.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      ['client_approved', 'approved'].includes(viewDetail.approval?.status) ? 'bg-emerald-100 text-emerald-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      Client Review
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                    <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      viewDetail.approval?.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      viewDetail.approval?.status === 'client_approved' ? 'bg-amber-100 text-amber-700' :
                      viewDetail.approval?.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-400'
                    }`}>
                      PM / Execution Review
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                    <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      viewDetail.approval?.status === 'approved' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'
                    }`}>
                      AI Implements
                    </div>
                  </div>
                </div>

                {/* Existing suggestions */}
                {viewDetail.approval?.suggestions?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-indigo-500" />
                      Suggestions & Feedback
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {viewDetail.approval.suggestions.map((s, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{s.user_name || 'Team'}</span>
                            <Badge className={s.role === 'client' ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'} variant="outline">
                              {s.role === 'client' ? 'Client' : 'Team'}
                            </Badge>
                            <span className="text-xs text-slate-400">{formatTimeAgo(s.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400">{s.suggestion}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add suggestion input */}
                {viewDetail.approval?.status !== 'approved' && viewDetail.approval?.status !== 'rejected' && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-violet-500" />
                      Add Suggestion
                    </p>
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Write your suggestion or feedback..."
                        value={suggestionText}
                        onChange={(e) => setSuggestionText(e.target.value)}
                        rows={2}
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        className="self-end gds-btn-primary"
                        disabled={!suggestionText.trim() || sendingSuggestion}
                        onClick={() => handleSendSuggestion(viewDetail.approval?.approval_id)}
                      >
                        {sendingSuggestion ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
